// src/hooks/useJobs.ts
import { useIonToast } from '@ionic/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import JobsService, { Job, JobFilters, JobSearchParams, SearchStats, UserSearch } from '../services/JobsService.ts';
import { jobSearch, API_URL } from '../services/api.ts';
import { supabase } from '../lib/supabaseClient.ts';

export interface ActiveSearchState {
  id?: string;
  query: string;
  status: string;
  isComplete: boolean;
  totalJobs?: number;
  tempId: string;
}

interface UseJobsReturn {
  // Data
  allJobs: Job[]; // Complete unfiltered job list (for filter options)
  jobs: Job[];
  displayedJobs: Job[];
  searchStats: SearchStats | null;
  userSearches: UserSearch[];

  // Loading states
  loading: boolean;
  initialLoading: boolean;
  searchesLoading: boolean;
  searchInProgress: boolean;
  searchProgress: string;
  activeSearches: ActiveSearchState[];

  // Pagination
  currentPage: number;
  hasMoreJobs: boolean;

  // Actions
  loadSearchResults: (params: JobSearchParams) => Promise<void>;
  loadUserSearches: () => Promise<void>;
  updateFilters: (filters: JobFilters) => void;
  loadMoreJobs: () => void;
  clearResults: () => void;
  revealUntilIndex: (index: number) => void;
  initiateNewSearch: (query: string, usePreferences?: boolean) => Promise<void>;
  dismissActiveSearch: () => void;
}

const JOBS_PER_PAGE = 20;

/**
 * Custom hook for managing job search state and operations.
 *
 * This hook uses the ref pattern to stabilize function dependencies and prevent
 * infinite render loops in parent components. All callback functions have empty
 * dependency arrays because they access external dependencies through refs.
 *
 * @example
 * ```tsx
 * const { jobs, loading, loadSearchResults } = useJobs();
 *
 * useEffect(() => {
 *   loadSearchResults({ searchId: '123', filters: {} });
 * }, [searchId, loadSearchResults]); // Safe - loadSearchResults never changes
 * ```
 */
export const useJobs = (): UseJobsReturn => {
  const [present] = useIonToast();

  // Refs for stable dependencies (prevents infinite loops in parent useEffects)
  const jobsServiceRef = useRef(JobsService.getInstance());
  const presentRef = useRef(present);

  useEffect(() => {
    presentRef.current = present;
  }, [present]);

  // Job data state
  const [allJobs, setAllJobs] = useState<Job[]>([]); // Complete unfiltered job list
  const [jobs, setJobs] = useState<Job[]>([]); // Filtered job list
  const [displayedJobs, setDisplayedJobs] = useState<Job[]>([]);
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);
  const [userSearches, setUserSearches] = useState<UserSearch[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [searchesLoading, setSearchesLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);

  // Manual search state (for new search initiated from Jobs page)
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [searchProgress, setSearchProgress] = useState('');
  const [activeSearches, setActiveSearches] = useState<ActiveSearchState[]>([]); // Changed to array
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const searchCompletedRef = useRef(false);

  // Updated to dismiss a specific search by its tempId
  const dismissActiveSearch = useCallback((tempId: string) => {
    setActiveSearches(prev => prev.filter(s => s.tempId !== tempId));
  }, []);

  /**
   * Loads job results for a specific search with caching support.
   * Uses cached results when available to avoid unnecessary API calls.
   *
   * @param params - Search parameters including searchId and filters
   * @stable This function never changes (empty deps) - safe to include in useEffect dependencies
   */
  const loadSearchResults = useCallback(async (params: JobSearchParams) => {
    const service = jobsServiceRef.current;
    const showToast = presentRef.current;

    const cached = service.getCachedResultsFor(params.searchId, params.filters);
    if (cached) {
      setAllJobs(cached.jobs); // Store complete unfiltered list
      setJobs(cached.jobs);
      setSearchStats(cached.stats);
      setDisplayedJobs(cached.jobs.slice(0, JOBS_PER_PAGE));
      setCurrentPage(1);
      setHasMoreJobs(cached.jobs.length > JOBS_PER_PAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log(`📥 Loading search results for:`, params);

    try {
      const result = await service.loadSearchResults(params);
      console.log(`📦 Loaded ${result.jobs.length} jobs`);

      setAllJobs(result.jobs); // Store complete unfiltered list
      setJobs(result.jobs);
      setSearchStats(result.stats);
      const firstPageJobs = result.jobs.slice(0, JOBS_PER_PAGE);
      setDisplayedJobs(firstPageJobs);
      setCurrentPage(1);
      setHasMoreJobs(result.jobs.length > JOBS_PER_PAGE);

      if (result.jobs.length === 0) {
        console.warn('⚠️ Zero jobs returned - search may still be processing');
      }
    } catch (error: any) {
      console.error('❌ Failed to load search results:', error);
      showToast({
        message: error?.message || 'Failed to load jobs. Please try again.',
        duration: 3000,
        color: 'danger'
      });
      setAllJobs([]);
      setJobs([]);
      setDisplayedJobs([]);
      setSearchStats(null);
      setCurrentPage(1);
      setHasMoreJobs(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Applies filters to the current job results (client-side filtering).
   * Resets pagination to first page after filtering.
   *
   * @param filters - Filter criteria for jobs
   * @stable This function never changes - safe to include in useEffect dependencies
   */
  const updateFilters = useCallback((filters: JobFilters) => {
    const service = jobsServiceRef.current;
    const filteredJobs = service.updateFilters(filters);

    setJobs(filteredJobs);

    const firstPageJobs = filteredJobs.slice(0, JOBS_PER_PAGE);
    setDisplayedJobs(firstPageJobs);
    setCurrentPage(1);
    setHasMoreJobs(filteredJobs.length > JOBS_PER_PAGE);

    setSearchStats(prev => ({
      count: filteredJobs.length,
      lastUpdated: prev?.lastUpdated || ''
    }));

    console.log(`🔍 Filters applied: ${filteredJobs.length} jobs shown`);
  }, []);

  /**
   * Loads the next page of jobs for infinite scroll.
   * No-op if already loading or no more jobs available.
   */
  const loadMoreJobs = useCallback(() => {
    if (!hasMoreJobs || loading) return;

    const nextPage = currentPage + 1;
    const startIndex = currentPage * JOBS_PER_PAGE;
    const endIndex = startIndex + JOBS_PER_PAGE;
    const nextBatch = jobs.slice(startIndex, endIndex);

    if (nextBatch.length > 0) {
      setDisplayedJobs(prev => [...prev, ...nextBatch]);
      setCurrentPage(nextPage);
      setHasMoreJobs(endIndex < jobs.length);
      console.log(`📄 Loaded page ${nextPage}: ${nextBatch.length} more jobs`);
    }
  }, [jobs, currentPage, hasMoreJobs, loading]);

  /**
   * Reveals jobs up to a specific index (for scroll restoration).
   * Useful when restoring scroll position to a job beyond the current page.
   *
   * @param index - Zero-based index of the job to reveal
   */
  const revealUntilIndex = useCallback((index: number) => {
    if (index < displayedJobs.length) return;
    const needed = index + 1;
    const nextSlice = jobs.slice(0, needed);
    setDisplayedJobs(nextSlice);
    setCurrentPage(Math.ceil(needed / JOBS_PER_PAGE));
    setHasMoreJobs(needed < jobs.length);
  }, [displayedJobs.length, jobs]);

  /**
   * Loads user's search history with caching support.
   *
   * @stable This function never changes - safe to include in useEffect dependencies
   */
  const loadUserSearches = useCallback(async () => {
    const service = jobsServiceRef.current;
    const showToast = presentRef.current;

    if (service.hasUserSearches()) {
      const cachedSearches = service.getCachedUserSearches();
      setUserSearches(cachedSearches);
      console.log(`📝 Using cached searches: ${cachedSearches.length}`);
      return;
    }

    setSearchesLoading(true);
    try {
      const searches = await service.getUserSearches();
      setUserSearches(searches);
      console.log(`📝 Loaded ${searches.length} searches from API`);

      if (searches.length === 0) {
        console.warn('⚠️ No searches returned - check if job search completed');
      }
    } catch (error: any) {
      console.error('❌ Failed to load user searches:', error);
      showToast({
        message: error?.message || 'Failed to load search history. Please try again.',
        duration: 3000,
        color: 'danger'
      });
      setUserSearches([]);
    } finally {
      setSearchesLoading(false);
    }
  }, []);

  /**
   * Clears all cached job data and resets state.
   *
   * @stable This function never changes - safe to include in useEffect dependencies
   */
  const clearResults = useCallback(() => {
    const service = jobsServiceRef.current;
    service.clearCache();
    setAllJobs([]);
    setJobs([]);
    setDisplayedJobs([]);
    setSearchStats(null);
    setCurrentPage(1);
    setHasMoreJobs(false);
    setLoading(false);
    setInitialLoading(false);
    console.log('🗑️ Jobs results cleared');
  }, []);

  /**
   * Initiates a new job search and polls for completion.
   * Used for manual searches from the Jobs page search bar.
   * Polls every 3 seconds for up to 3 minutes.
   *
   * @param query - Natural language job search query
   * @stable This function depends on other stable callbacks
   */
  /**
   * Initiates a new job search and connects to SSE stream for updates.
   *
   * @param query - Natural language job search query
   * @stable This function depends on other stable callbacks
   */
  const initiateNewSearch = useCallback(async (query: string, usePreferences: boolean = true) => {
    const service = jobsServiceRef.current;
    const showToast = presentRef.current;
    const tempId = Date.now().toString() + Math.random().toString().slice(2, 6);
    let isMergedWithHistory = false; // Track if we merged it to history/results

    // Initial State for THIS search
    setSearchInProgress(true); // Global loading state (optional, keeps UI responsive)
    setSearchProgress('Starting job search...');

    // Add to active searches
    setActiveSearches(prev => [{ tempId, query, status: 'Starting job search...', isComplete: false }, ...prev]);

    // Helper to update THIS specific search instance
    const updateSearch = (updates: Partial<ActiveSearchState>) => {
      setActiveSearches(prev => prev.map(s => s.tempId === tempId ? { ...s, ...updates } : s));
    };

    let eventSource: EventSource | null = null;
    let pollInterval: NodeJS.Timeout | null = null;
    let localCompleted = false;

    // Helper to handle search completion
    const handleSearchCompletion = async (searchId: string, totalJobs: number) => {
      localCompleted = true;

      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }

      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }

      const successMsg = totalJobs > 0 ? `Found ${totalJobs} jobs!` : 'Search completed';
      if (!isMergedWithHistory) setSearchProgress(successMsg);

      // Update Active Search State to complete
      updateSearch({
        status: successMsg,
        isComplete: true,
        totalJobs,
        id: searchId
      });

      // Refresh lists immediately
      service.invalidateSearchesCache();
      try {
        await loadUserSearches();

        if (totalJobs > 0) {
          await loadSearchResults({
            searchId: searchId,
            filters: { location: '', jobType: '', salary: '', datePosted: '' }
          });

          showToast({
            message: `Found ${totalJobs} jobs!`,
            duration: 3000,
            color: 'success'
          });
        } else {
          showToast({
            message: 'Search completed but found no new jobs.',
            duration: 3000,
            color: 'warning'
          });
        }
      } catch (e) {
        console.error('Error refreshing results:', e);
      }
      isMergedWithHistory = true;
      setSearchInProgress(false);
      setSearchProgress('');
    };

    try {
      console.log(`🔍 Initiating search: "${query}" (usePreferences: ${usePreferences})`);

      // 1. Kick off the search via API
      const searchResult = await jobSearch.searchJobs(query, usePreferences);

      console.log('🔍 Search initiated result:', searchResult);

      // Allow new searches immediately
      setSearchInProgress(false);

      let userSearchQueryId = searchResult?.user_search_query_id || searchResult?.search_details?.user_search_query_id;
      const parseTaskId = searchResult?.parse_task_id || searchResult?.search_details?.parse_task_id;

      // If we don't have a direct search ID (likely a new search), we must wait for the parse task to finish
      // to get the user_search_query_id, which we need for streaming.
      if (!userSearchQueryId && parseTaskId) {
        setSearchProgress('Analyzing your request...');
        updateSearch({ status: 'Analyzing your request...' });
        console.log(`⏳ Waiting for parse task ${parseTaskId} to complete...`);

        // Poll for task completion to get the ID
        let attempts = 0;
        const maxAttempts = 40; // 40 seconds timeout
        while (!userSearchQueryId && attempts < maxAttempts) {
          await new Promise(r => setTimeout(r, 1000));
          attempts++;
          try {
            const status = await jobSearch.getSearchStatus(parseTaskId);
            console.log(`🔄 Parsing Poll ${attempts}/${maxAttempts}:`, status);

            if (status.user_search_query_id) {
              userSearchQueryId = status.user_search_query_id;
              console.log(`✅ Got user_search_query_id from task: ${userSearchQueryId}`);
            } else if (status.completed && !status.success) {
              throw new Error(status.error || 'Search initialization failed');
            }
          } catch (e) {
            console.warn('Error polling for search ID:', e);
          }
        }
      }

      if (!userSearchQueryId) {
        console.warn('Could not get search ID for streaming, continuing in background');
        setSearchInProgress(false);
        setSearchProgress('');

        // Update widget to indicate background processing instead of clearing
        updateSearch({
          status: 'Request processing in background. Check History shortly.',
          isComplete: true, // Mark complete so user can dismiss
          totalJobs: 0
        });

        showToast({
          message: 'Search started in background. Check your history shortly for results.',
          duration: 5000,
          color: 'success'
        });
        return;
      }

      console.log(`📡 Connecting to stream for: ${userSearchQueryId}`);
      setSearchProgress('Connecting to search stream...');
      updateSearch({ status: 'Connecting to search stream...', id: userSearchQueryId });

      // 2. Get auth token for SSE authentication
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || localStorage.getItem('authToken');

      // 3. Connect to SSE endpoint
      const streamUrl = `${API_URL}/stream/job-search/${userSearchQueryId}?token=${token}`;
      console.log('🔗 Connecting to SSE URL:', streamUrl);
      console.log('🔑 Token length:', token ? token.length : 0);
      eventSource = new EventSource(streamUrl);

      // Handle stream messages
      eventSource.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 SSE Received:', data);

          if (data.type === 'job_search_progress') {
            // Update progress text
            const statusMsg = `${data.status} ${data.progress ? `(${data.progress}%)` : ''}`;
            if (!isMergedWithHistory) setSearchProgress(statusMsg);
            updateSearch({ status: statusMsg });

          } else if (data.type === 'job_search_complete') {
            console.log('✅ Search completion received via SSE');
            const totalJobs = data.total_jobs || 0;
            // Use local var here as it's definitely defined in this scope
            await handleSearchCompletion(userSearchQueryId, totalJobs);
          } else if (data.type === 'error') {
            console.error('❌ SSE Error:', data.message);
            eventSource?.close();
          }
        } catch (e) {
          console.error('Error parsing SSE message:', e);
        }
      };

      eventSource.onerror = (err) => {
        console.warn('⚠️ SSE Connection error/closed:', err);
      };

      // Failsafe timeout for the STREAM connection specifically
      setTimeout(() => {
        if (localCompleted) return;

        console.warn('⏰ Stream timeout (1min), releasing UI...');
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }

        setSearchInProgress(false);
        setSearchProgress('');

        // Don't clear active search, just mark as timed out/background
        updateSearch({
          status: 'Taking longer than expected. Check history shortly.',
          isComplete: true, // Allow dismissal
          totalJobs: 0
        });

        showToast({
          message: 'Search is taking longer than expected. Check your history shortly.',
          duration: 5000,
          color: 'warning'
        });
      }, 60000);

    } catch (error: any) {
      console.error('❌ Failed to initiate search:', error);
      if (eventSource) eventSource.close();

      setSearchInProgress(false);
      setSearchProgress('');
      updateSearch({ status: 'Search failed to start.', isComplete: true });

      showToast({
        message: error?.response?.data?.detail || 'Failed to start search. Please try again.',
        duration: 3000,
        color: 'danger'
      });
    }
  }, [loadUserSearches, loadSearchResults]);
  // ✅ Stable - only depends on other stable callbacks

  return {
    // Data
    allJobs,
    jobs,
    displayedJobs,
    searchStats,
    userSearches,

    // Loading states
    loading,
    initialLoading,
    searchesLoading,
    searchInProgress,
    searchProgress,

    // Pagination
    currentPage,
    hasMoreJobs,

    // Actions
    loadSearchResults,
    loadUserSearches,
    updateFilters,
    loadMoreJobs,
    clearResults,
    revealUntilIndex,
    initiateNewSearch,
    dismissActiveSearch,
    activeSearches
  };
};

export default useJobs;